<template>
  <div class="lesson-page container py-5">
    <div class="text-center mb-4">
      <h2 class="fw-bold">地產小學堂</h2>
      <p class="lead text-muted">長發地產｜知識百科與新聞總覽</p>
    </div>

    <!-- 分類切換 -->
    <ul class="nav nav-pills justify-content-center gap-2 mb-4 lesson-tabs" role="tablist">
      <li class="nav-item" role="presentation">
        <button :class="['nav-link', currentTab==='all' ? 'active' : '']" @click="switchTab('all')" type="button" role="tab">全部文章</button>
      </li>
      <li class="nav-item" role="presentation">
        <button :class="['nav-link', currentTab==='wiki' ? 'active' : '']" @click="switchTab('wiki')" type="button" role="tab">知識百科</button>
      </li>
      <li class="nav-item" role="presentation">
        <button :class="['nav-link', currentTab==='news' ? 'active' : '']" @click="switchTab('news')" type="button" role="tab">新聞總覽</button>
      </li>
    </ul>

    <!-- 篩選與搜尋 -->
    <div class="row g-2 align-items-center mb-3">
      <div class="col-12 col-md-6 order-2 order-md-1">
        <div class="btn-group lesson-filter" role="group" aria-label="篩選">
          <button type="button" class="btn btn-outline-secondary btn-sm" :class="{active: sortBy==='new'}" @click="sortBy='new'">最新</button>
          <button type="button" class="btn btn-outline-secondary btn-sm" :class="{active: sortBy==='old'}" @click="sortBy='old'">最舊</button>
        </div>
      </div>
      <div class="col-12 col-md-6 order-1 order-md-2">
        <div class="input-group">
          <input type="text" class="form-control" placeholder="搜尋關鍵字..." v-model.trim="keyword" @input="handleSearch" />
          <button class="btn btn-outline-secondary" @click="clearSearch" v-if="keyword">清除</button>
        </div>
      </div>
    </div>

    <!-- 文章清單 -->
    <div class="row g-4">
      <div class="col-12 col-md-6 col-lg-4" v-for="item in paginatedList" :key="item.id">
        <router-link :to="`/post/${item.slug}`" class="text-decoration-none">
          <article class="lesson-card">
            <div class="article-thumbnail" :style="item.thumbnail ? { backgroundImage: `url(${item.thumbnail})` } : {}"></div>
            <div class="article-content">
              <h5 class="article-title">{{ item.title }}</h5>
              <p class="article-excerpt">{{ item.summary }}</p>
              <div class="article-date">{{ item.date }}</div>
            </div>
          </article>
        </router-link>
      </div>
    </div>

    <!-- 分頁器 -->
    <nav class="mt-4" aria-label="pager" v-if="totalPages > 1">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{disabled: page===1}">
          <button class="page-link" @click="goPage(page-1)" :disabled="page===1">上一頁</button>
        </li>
        <li class="page-item" v-for="p in totalPages" :key="p" :class="{active: p===page}">
          <button class="page-link" @click="goPage(p)">{{ p }}</button>
        </li>
        <li class="page-item" :class="{disabled: page===totalPages}">
          <button class="page-link" @click="goPage(page+1)" :disabled="page===totalPages">下一頁</button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head';

export default {
  name: 'LessonPage',
  setup() {
    useHead({
      title: '地產小學堂 | 房地產知識百科與新聞 - 長發地產',
      meta: [
        {
          name: 'description',
          content: '長發地產小學堂提供專業房地產知識百科與最新新聞資訊，包含區段徵收、容積率建蔽率、都市更新、危老重建、土地使用分區、實價登錄等實用知識，以及桃園航空城、捷運綠線開發等在地新聞。'
        },
        {
          name: 'keywords',
          content: '地產小學堂,房地產知識,區段徵收,容積率,建蔽率,都市更新,危老重建,土地使用分區,實價登錄,桃園航空城,捷運綠線,房地產新聞'
        },
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: '地產小學堂 | 房地產知識百科與新聞' },
        { property: 'og:description', content: '提供專業房地產知識與最新開發資訊' },
        { property: 'og:image', content: '/區段徵收.png' },
        { name: 'twitter:card', content: 'summary_large_image' }
      ],
      script: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: '地產小學堂',
            description: '長發地產提供的房地產知識百科與新聞資訊',
            publisher: {
              '@type': 'Organization',
              name: '長發地產'
            }
          })
        }
      ]
    });
  },
  data() {
    return {
      currentTab: 'all',
      keyword: '',
      sortBy: 'new',
      page: 1,
      pageSize: 9,
      items: [
        // 知識百科
        { id: 1, slug: 'knowledge-1', category: 'wiki', date: '2024.10.15', title: '什麼是區段徵收？', summary: '政府為了新都市開發建設、舊都市更新或其他開發目的需要，對於一定區域內之土地全部予以徵收，並重新規劃整理。了解區段徵收對土地所有權人的權益影響。', thumbnail: '/區段徵收.png' },
        { id: 2, slug: 'knowledge-2', category: 'wiki', date: '2024.10.10', title: '容積率與建蔽率怎麼算？', summary: '建蔽率是建築物投影面積與基地面積的比率，容積率則是建築物總樓地板面積與基地面積的比率。這兩個指標直接影響土地的開發價值。', thumbnail: '/容積率建蔽率.png' },
        { id: 3, slug: 'knowledge-3', category: 'wiki', date: '2024.10.08', title: '都市更新與危老重建差異', summary: '都市更新需多數所有權人同意，程序較複雜但獎勵較多；危老重建門檻較低，可100%同意即可實施，但獎勵有時效限制。選擇適合的重建方案很重要。', thumbnail: '/都市更新危老重建.png' },
        { id: 4, slug: 'knowledge-4', category: 'wiki', date: '2024.09.25', title: '土地使用分區與地目說明', summary: '土地使用分區分為住宅區、商業區、工業區等，地目則有建地、農地、林地等分類。了解土地分區管制與地目，是土地交易的基礎知識。', thumbnail: '/土地使用分區.png' },
        { id: 5, slug: 'knowledge-5', category: 'wiki', date: '2024.09.18', title: '土地增值稅如何計算？', summary: '土地增值稅是土地所有權移轉時，就土地漲價部分課徵的稅賦。分為一般稅率與自用住宅優惠稅率，善用優惠稅率可大幅節省稅負。', thumbnail: '/土地增值稅.png' },
        { id: 6, slug: 'knowledge-6', category: 'wiki', date: '2024.09.12', title: '不動產實價登錄查詢技巧', summary: '實價登錄提供真實的不動產交易價格資訊，是買賣雙方議價的重要參考。學會查詢與分析實價登錄資料，能幫助您做出更明智的決策。', thumbnail: '/實價登錄查詢技巧.png' },
        { id: 7, slug: 'knowledge-7', category: 'wiki', date: '2024.09.05', title: '農地買賣注意事項', summary: '農地交易有特殊規定，需注意農地農用、毗鄰耕地優先購買權、農民身分認定等問題。了解農地相關法規，避免交易糾紛。', thumbnail: '/農地買賣注意事項.png' },
        { id: 8, slug: 'knowledge-8', category: 'wiki', date: '2024.08.28', title: '地上權與所有權的差別', summary: '地上權是在他人土地上擁有建築物的權利，不同於完整的土地所有權。地上權住宅價格較低，但有使用年限與貸款限制需注意。', thumbnail: '/地上權所有權.png' },
        { id: 9, slug: 'knowledge-9', category: 'wiki', date: '2024.08.20', title: '重劃與徵收的區別', summary: '重劃是透過土地交換分合，改善土地利用；徵收則是政府強制取得私有土地。兩者對地主的權益影響不同，補償方式也有差異。', thumbnail: '/重劃與徵收的區別.png' },
        // 新聞總覽
        { id: 28, slug: 'news-18', category: 'news', date: '2026.05.27', title: '三鶯線延伸八德工程啟動 張善政：123年朝通車目標邁進', summary: '捷運三鶯線延伸八德計畫基本設計經費160.57億元審議通過，未來與桃園捷運綠線G04大湳站共構銜接，串聯桃園與雙北軌道路網，朝123年通車目標穩步邁進。', thumbnail: '/news-18.jpg' },
        { id: 27, slug: 'news-17', category: 'news', date: '2026.05.27', title: '🚀桃園航空城95公頃產專區｜正式進入「精準招商」階段‼️', summary: '桃園市政府正式啟動航空城95公頃產專區精準招商規劃，KPMG得標，航空城正式從土地開發階段轉向產業定位規劃期，朝向「3低1高」低碳產業園區方向推進。', thumbnail: '/news-17.jpg' },
        { id: 26, slug: 'news-16', category: 'news', date: '2026.03.15', title: "區段徵收G12、G13、G14 開發案『市都委會第一次審議進度解析』", summary: '桃園捷運綠線G12、G13、G14車站周邊土地開發案，近期進入市都委會實質審議階段。115年2月25日召開第一次專案小組審議，並產生會議紀錄，審議正式啟動。', thumbnail: '/G121314.jpg' },
        { id: 20, slug: 'news-10', category: 'news', date: '2025.11.18', title: '桃園航空城優先產業專用區 I 基地開發計畫 - 台壽、榮運攜手打造國際級巨蛋與產業城', summary: '台灣人壽與榮運合作開發桃園航空城優先產業專用區，總投資超過500億元，將打造國際級巨蛋場館、倉儲物流、旅宿及商場，形成18.9公頃複合式產業城。', thumbnail: '/news-10.png' },
        { id: 21, slug: 'news-11', category: 'news', date: '2025.11.17', title: '經國特區好消息！幸福路跨南崁溪「人行跨橋」正式啟動', summary: '桃園市政府水務局公告「北溪溪幸福路人行跨橋」工程說明會，此跨橋將串連經國特區與南崁溪兩岸，提升居民生活便利性，結合G12捷運站與經國轉運站，強化區域發展優勢。', thumbnail: '/news-11.jpg' },
        { id: 22, slug: 'news-12', category: 'news', date: '2025.11.14', title: '經國特區 × G12 G13 G14｜啟動桃園黃金10年', summary: '經國特區位於北桃園交通門戶，結合國道、經國轉運站與捷運綠線G12站，從工業邊陲轉型為城市核心。南崁溪水岸升級、指標建商進駐，未來結合TOD開發與第二行政園區規劃，將成為桃園新CBD。', thumbnail: '/G121314.jpg' },
        { id: 23, slug: 'news-13', category: 'news', date: '2025.11.13', title: '桃園重大產業用地標售｜桃科二期 46,682 坪正式釋出', summary: '桃園科技工業園區第二期首波釋出46,682坪產業用地，位於北台灣科技走廊核心，採先租後售模式，企業僅需15%即可進駐，提供1.5年建照緩衝期及分期付款，投標截止至2025年1月13日。', thumbnail: '/news-13.jpg' },
        { id: 24, slug: 'news-14', category: 'news', date: '2025.11.06', title: '桃園建設大紀事｜國道2號「大竹交流道改善工程」正式動土', summary: '國道2號大竹交流道改善工程於11/3正式動土，總經費13.59億元，將新建高架匝道橋跨越中正東路，紓解匝道壅塞問題，縮短通勤時間，串聯桃園都會環線交通網。', thumbnail: '/news-14.jpg' },
        { id: 25, slug: 'news-15', category: 'news', date: '2025.11.03', title: '桃園航空城｜又一重大工程正式啟動招標！', summary: '桃園航空城計畫區段徵收工程（自貿港、崙仔後及A3周邊）於10/28正式招標，總經費33.16億元，涵蓋95公頃基地基礎工程，預計12/2開標，工期1,150天。', thumbnail: '/news-15.jpg' },
        { id: 10, slug: 'news-1', category: 'news', date: '2025.10.19', title: '結合航空城！桃園蘆竹誠聖段800戶社宅決標 2027年動工', summary: '蘆竹區誠聖段社宅預計提供近800戶，位於機場園區特定區，距機捷A11站僅600公尺。規劃一房型50%、二房型30%、三房型20%，整合托育中心與商業空間，預計2027年動工。', thumbnail: 'https://p1-news.hfcdn.com/p1-news/NzU1MzAyN25ld3M,/c5b3b4f9ddd24dde_1593x882.jpg/qs/w=1200&h=1200&r=16888' },
        { id: 11, slug: 'news-2', category: 'news', date: '2025.10.20', title: '航空城拆遷戶貸款卡關，是被信用管制措施害慘了？央行最新回應出爐', summary: '桃園航空城拆遷戶申請房貸遇困難，央行澄清信用管制措施並未針對自住需求，銀行應依個案審核。專家建議拆遷戶備妥財力證明，並多方比較貸款方案。', thumbnail: 'https://s.yimg.com/ny/api/res/1.2/eIzsxps3PqcbFQq1WF6L2w--/YXBwaWQ9aGlnaGxhbmRlcjt3PTk2MDtoPTU0MDtjZj13ZWJw/https://media.zenfs.com/en/stormmediagroup.com/858e0222285420d4256f74f3520a9173' },
        { id: 12, slug: 'news-3', category: 'news', date: '2025.02.25', title: '桃園航空城計畫重大進度曝！「先建後遷」期程出爐、不再展延', summary: '行政院強調「先建後遷」期程2026年底前完成不再展延。安置街廓已啟動點交，安置住宅陸續取得使用執照，配合第三跑道工程依計畫推進。', thumbnail: 'https://p1-news.hfcdn.com/p1-news/MzM2MzE4NG5ld3M,/67164d894eb9926c_934x437.jpg/qs/w=1200&h=1200&r=16888' },
        { id: 13, slug: 'news-4', category: 'news', date: '2024.09.06', title: '桃園捷運綠線G12站土地開發計畫啟動', summary: '桃園捷運綠線G12站周邊土地開發計畫正式啟動，預期將帶動中壢地區發展，提升區域房地產價值與生活機能。', thumbnail: 'https://hakkanews.tw/wp-content/uploads/2024/09/hakkanews.tw-2024-09-06_17-13-37_364238-jpg.webp' },
        { id: 14, slug: 'news-5', category: 'news', date: '2024.09.06', title: '桃園捷運綠線機廠工程進度超前', summary: '桃園捷運綠線機廠工程進度順利，預計2026年完工通車。機廠將提供列車維修與停放功能，是綠線營運的重要設施。', thumbnail: 'https://imgcdn.cna.com.tw/www/WebPhotos/1024/20240906/1151x768_wmkn_0_C20240906000095.jpg' },
        { id: 15, slug: 'news-6', category: 'news', date: '2024.09.05', title: '台鐵中路站周邊都市計畫啟動，124公頃土地開發', summary: '台鐵中路站周邊124公頃土地開發計畫正式啟動，將成為桃園重要發展區域，結合鐵路運輸與都市發展優勢。', thumbnail: 'https://p1-news.hfcdn.com/p1-news/MzQ4OTIzN25ld3M,/f1365e836e469fde_1250x938.jpg/qs/w=1200&h=1200&r=16888' },
        { id: 17, slug: 'news-7', category: 'news', date: '2025.01.06', title: '桃園都市更新獎勵加碼，防災型重建最高獎勵容積50%', summary: '桃園市府修正都市計畫法施行細則，提高防災型都更重建獎勵容積至最高50%，加速舊城再生，並新增可負擔住宅法源依據。', thumbnail: 'https://urbanrenewal.wealth.com.tw/uploads/editor/1757645926.png' },
        { id: 18, slug: 'news-8', category: 'news', date: '2024.08.20', title: '桃園小檜溪重劃區房價飆漲，吸引首購族進駐', summary: '小檜溪重劃區因交通便利、生活機能完善，房價持續上漲，吸引大量首購族進駐，成為桃園新興熱門區域。', thumbnail: 'https://p1-news.hfcdn.com/p1-news/NzI5NzQzMm5ld3M,/a57a3b9a71924c94_450x337.jpg' },
        { id: 19, slug: 'news-9', category: 'news', date: '2024.10.20', title: '桃園捷運綠線3站要開發！近千人湧入TOD說明會', summary: '桃園捷運綠線G07、G08、G09三站TOD開發說明會吸引近千位民眾參與，顯示當地居民高度關注，預期將帶動周邊房市發展。', thumbnail: 'https://s.yimg.com/ny/api/res/1.2/WgFRMMkYAaenDP0gaPTy.Q--/YXBwaWQ9aGlnaGxhbmRlcjt3PTk2MDtoPTcyMDtjZj13ZWJw/https://media.zenfs.com/ko/chinatimes.com.tw/386024570aa153ff1b8450333b64cf16' }

      ]
    }
  },
  computed: {
    filteredList() {
      let list = this.items.slice();
      if (this.currentTab !== 'all') {
        list = list.filter(i => i.category === this.currentTab);
      }
      if (this.keyword) {
        const k = this.keyword.toLowerCase();
        list = list.filter(i => (i.title + i.summary).toLowerCase().includes(k));
      }
      list.sort((a, b) => {
        const toNum = (d) => Number((d || '').split('.').join('')) || 0; // 2024.05.06 -> 20240506
        const an = toNum(a.date);
        const bn = toNum(b.date);
        return this.sortBy === 'new' ? (bn - an) : (an - bn);
      });
      return list;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredList.length / this.pageSize));
    },
    paginatedList() {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredList.slice(start, start + this.pageSize);
    }
  },
  methods: {
    switchTab(tab) {
      this.currentTab = tab;
      this.page = 1;
    },
    handleSearch() {
      this.page = 1;
    },
    clearSearch() {
      this.keyword = '';
      this.page = 1;
    },
    goPage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
}
</script>

<style scoped>
.lesson-page { 
  min-height: 60vh; 
  padding-top: 100px;
}
.lesson-tabs .nav-link { background: #f1f3f5; color: #333; }
.lesson-tabs .nav-link.active { background: #0d6efd; color: #fff; }
.lesson-filter .btn.active { color: #fff; background: #6c757d; }

.lesson-card {
  background: white;
  border: 2px solid #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 380px;
}

.lesson-card:hover {
  border-color: #007bff;
  box-shadow: 0 8px 20px rgba(0, 123, 255, 0.15);
  transform: translateY(-5px);
}

.article-thumbnail {
  width: 100%;
  padding-top: 56.25%; /* 16:9 比例 */
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
}

.article-content {
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  flex: 1;
  position: relative;
}

.article-date {
  color: #999;
  font-size: 0.85rem;
  position: absolute;
  bottom: 1.25rem;
  right: 1.25rem;
  margin: 0;
}

.article-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.75rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.article-excerpt {
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0 0 2.5rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lesson-card:hover .article-title {
  color: #007bff;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .lesson-page {
    padding-top: 80px;
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  .lesson-tabs {
    flex-wrap: wrap;
  }
  
  .lesson-tabs .nav-link {
    font-size: 0.95rem;
    padding: 0.5rem 1rem;
  }
  
  .lesson-filter .btn {
    font-size: 0.9rem;
  }
  
  .lesson-card {
    height: auto;
    min-height: 350px;
  }
}

@media (max-width: 576px) {
  .lesson-page {
    padding-top: 70px;
  }
  
  h2.fw-bold {
    font-size: 1.75rem;
  }
  
  .lead {
    font-size: 1rem;
  }
  
  .row.g-2 {
    flex-direction: column-reverse;
  }
  
  .input-group {
    margin-bottom: 1rem;
  }
}
</style>


